import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../auth/useAuth0';
import { useAlert } from '../../context/alert/AlertContext';
import { BusinessSummary } from '../../model/business/BusinessSummary';
import { BusinessDetailsInput } from '../../service/business/BusinessDetailsInput';
import { createBusiness, saveBusinessDetails, saveBusinessHours, requestCnpjOtp, verifyCnpjOtp, findBusinessSettingByBusinessId, saveBusinessSettings } from '../../service/business/business-service';
import { BusinessSettingsInput } from '../../service/business/BusinessSettingsInput';
import { HEALTH_RECORDS_ENABLED } from '../../util/healthRecords';
import { brStateToUf, timezoneFor } from '../../util/businessTimezone';
import { mapSourceToTarget } from '../../util/util';
import { BusinessHoursInput } from '../../service/business/BusinessHoursInput';
import { mediaUrl } from '../../service/media/media-service';
import { BR_STATES } from '../../util/brStates';
import {
    lookupCnpj, isValidCnpj, formatCnpj, cleanCnpj, onlyDigits, CnpjNotFoundError, CnpjLookupResult,
} from '../../service/cnpj/cnpj-service';
import { lookupCep, formatCep, lookupCaPostal, formatCaPostal } from '../../service/cep/cep-service';
import { canonicalCountry } from '../../util/cep';
import { geocodeSuggest, GeoSuggestion } from '../../service/geo/geo-service';
import TimeSelect from '../commons/datetime/TimeSelect';
import './BusinessOnboardingWizard.css';

interface WizardForm {
    name: string;
    /** CNPJ (formatted) + razão social, captured when the owner starts from CNPJ. */
    cnpj: string;
    legalName: string;
    /** Extra Receita fields persisted alongside the business. */
    registeredEmail: string;
    registeredPhone: string;
    cnae: string;
    cnpjSnapshot: string;
    category: string;
    /** Free-text service when category === 'Outro'. */
    categoryOther: string;
    /** ISO-3166 alpha-2 country. Drives CEP autofill (BR only) + the state field. */
    country: string;
    city: string;
    state: string;
    /** Structured address: logradouro / número / complemento (sala) / bairro. */
    address1: string;
    number: string;
    complement: string;
    neighborhood: string;
    zipCode: string;
    /** Exact coordinates of the picked address suggestion; null when typed manually. */
    latitude: number | null;
    longitude: number | null;
    headline: string;
    about: string;
    yearsExperience: string;
    highlight: string;
    brandColor: string;
    coverImage: string;
    instagram: string;
    /** Prontuário master switch answer: '' (unanswered) | 'yes' | 'no'. */
    healthAnswer: '' | 'yes' | 'no';
}

const EMPTY: WizardForm = {
    name: '', cnpj: '', legalName: '', registeredEmail: '', registeredPhone: '', cnae: '', cnpjSnapshot: '',
    category: '', categoryOther: '', country: 'BR', city: '', state: '', address1: '', number: '', complement: '', neighborhood: '', zipCode: '',
    latitude: null, longitude: null,
    headline: '', about: '', yearsExperience: '', highlight: '',
    brandColor: '#7266E8', coverImage: '', instagram: '',
    healthAnswer: '',
};

/** Countries the platform actually supports today (address autofill + currency).
 *  Kept intentionally short — BR (full CEP autofill) and CA (postal → city/UF).
 *  `nameKey` is an i18n key so the label follows the UI language. */
const COUNTRIES: { code: string; nameKey: string; currency: string }[] = [
    { code: 'BR', nameKey: 'onboarding.step3.countryBR', currency: 'BRL' },
    { code: 'CA', nameKey: 'onboarding.step3.countryCA', currency: 'CAD' },
];
const currencyForCountry = (code: string): string =>
    COUNTRIES.find((c) => c.code === code)?.currency ?? 'BRL';

/** Fake Google Business profiles — placeholder data until the real Google
 *  Business Profile API integration (OAuth + locations) is wired up. */
const FAKE_GOOGLE_BUSINESSES: Partial<WizardForm>[] = [
    { name: 'Studio Bella', category: 'Cabelo', headline: 'Salão de beleza completo', address1: 'Av. Paulista', number: '1000', neighborhood: 'Bela Vista', city: 'São Paulo', state: 'SP', zipCode: '01310-100' },
    { name: 'Barbearia Dom Corleone', category: 'Barbearia', headline: 'Barbearia clássica', address1: 'Rua Augusta', number: '250', neighborhood: 'Consolação', city: 'São Paulo', state: 'SP', zipCode: '01304-000' },
];

/** value = the stored category (stable key, kept in PT for existing data);
 *  labelKey = i18n key so the chip label follows the UI language. */
const CATEGORIES: { value: string; labelKey: string }[] = [
    { value: 'Cabelo', labelKey: 'hair' },
    { value: 'Estética', labelKey: 'aesthetics' },
    { value: 'Spa', labelKey: 'spa' },
    { value: 'Clínica', labelKey: 'clinic' },
    { value: 'Maquiagem', labelKey: 'makeup' },
    { value: 'Massagem', labelKey: 'massage' },
    { value: 'Unhas', labelKey: 'nails' },
    { value: 'Barbearia', labelKey: 'barbershop' },
    { value: 'Fitness', labelKey: 'fitness' },
    { value: 'Fotografia', labelKey: 'photography' },
    { value: 'Outro', labelKey: 'other' },
];

const STEPS = ['Boas-vindas', 'Nome', 'Serviço', 'Local', 'Horários', 'Sobre', 'Marca', 'Revisão'];

/** Version of the veracity declaration accepted at creation. Bump when the text changes. */
const TERMS_VERSION = 'v1';

/** Days for the working-hours step. Mon–Fri default open, weekend closed. */
const DAYS: { key: string; label: string; open: boolean }[] = [
    { key: 'MONDAY', label: 'Segunda', open: true },
    { key: 'TUESDAY', label: 'Terça', open: true },
    { key: 'WEDNESDAY', label: 'Quarta', open: true },
    { key: 'THURSDAY', label: 'Quinta', open: true },
    { key: 'FRIDAY', label: 'Sexta', open: true },
    { key: 'SATURDAY', label: 'Sábado', open: false },
    { key: 'SUNDAY', label: 'Domingo', open: false },
];

interface DayHours { open: boolean; openTime: string; closeTime: string; }

/** Mask a phone for display: keep DDD + first digit, hide the rest.
 *  "(21) 96758828" → "(21) 9****-****". */
function maskPhone(phone: string): string {
    const digits = (phone || '').replace(/\D+/g, '');
    if (digits.length < 10) return phone || '';
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)}****-****`;
}

/** Mask an email for display: "joao@gmail.com" → "j****@gmail.com". */
function maskEmail(email: string): string {
    const at = (email || '').indexOf('@');
    if (at <= 0) return email || '';
    return `${email.slice(0, 1)}****${email.slice(at)}`;
}

/** Whether an email string is usable. */
function hasUsableEmail(email?: string): boolean {
    return !!email && email.includes('@');
}

/** "09:00" / "18:30" (24h from <input type=time>) → "09:00 AM" / "06:30 PM"
 *  (the 12-hour format the business-service parser expects). */
function to12h(hhmm: string): string {
    const [hRaw, mRaw] = (hhmm || '09:00').split(':');
    const h = Number(hRaw);
    const m = (mRaw ?? '00').padStart(2, '0');
    const ap = h < 12 ? 'AM' : 'PM';
    const h12 = ((h + 11) % 12) + 1; // 0→12, 12→12, 13→1
    return `${String(h12).padStart(2, '0')}:${m} ${ap}`;
}

interface Props {
    /** Called with the freshly created business so the shell can select it. */
    onDone: (created: BusinessSummary) => void;
    /** Back out of the wizard (only meaningful when the user already has businesses). */
    onCancel: () => void;
    /** Whether a cancel/exit option is offered. */
    allowCancel?: boolean;
}

const BusinessOnboardingWizard: React.FC<Props> = ({ onDone, onCancel, allowCancel }) => {
    const { t, i18n } = useTranslation();
    /** Manual language pick in onboarding (before any business language exists).
     *  Persisted to gs-lang so it survives reloads and wins over the browser default. */
    const setLang = (lng: 'pt' | 'en') => {
        i18n.changeLanguage(lng);
        try { localStorage.setItem('gs-lang', lng); } catch { /* ignore */ }
    };
    const { getAccessTokenSilently } = useAuth0();
    const { setAlert } = useAlert();

    const [step, setStep] = useState(0);
    const [form, setForm] = useState<WizardForm>(EMPTY);
    const [saving, setSaving] = useState(false);
    // Veracity declaration acceptance (required to create — see the Revisão step).
    const [agreedTerms, setAgreedTerms] = useState(false);

    // Working hours (optional — can be skipped and set later in Settings → Calendar).
    const [hours, setHours] = useState<Record<string, DayHours>>(() =>
        Object.fromEntries(DAYS.map((d) => [d.key, { open: d.open, openTime: '09:00', closeTime: '18:00' }]))
    );
    const [skipHours, setSkipHours] = useState(false);

    // Method chooser (step 0): null = the 3-card chooser, else a sub-view.
    const [subView, setSubView] = useState<null | 'cnpj' | 'google'>(null);
    // CNPJ sub-view state.
    const [cnpjInput, setCnpjInput] = useState('');
    const [cnpjLoading, setCnpjLoading] = useState(false);
    const [cnpjError, setCnpjError] = useState('');
    const [cnpjResult, setCnpjResult] = useState<CnpjLookupResult | null>(null);
    // CNPJ phase: 'lookup' = enter/search CNPJ; 'verify' = ownership OTP.
    const [cnpjPhase, setCnpjPhase] = useState<'lookup' | 'verify'>('lookup');
    // OTP state for the verify phase. 'choose' = pick email vs SMS (both available);
    // 'smsdemo' = phone-only fake SMS PIN (no real provider yet — UI demo).
    const [otpStatus, setOtpStatus] = useState<'idle' | 'choose' | 'sending' | 'sent' | 'smsdemo' | 'auto' | 'nocontact'>('idle');
    const [otpMasked, setOtpMasked] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [otpError, setOtpError] = useState('');
    const [otpVerifying, setOtpVerifying] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    // Google sub-view state (fake for now).
    const [googleConnecting, setGoogleConnecting] = useState(false);
    const [googleConnected, setGoogleConnected] = useState(false);

    const [cepLoading, setCepLoading] = useState(false);
    /** Street-field address autocomplete (MapTiler via discovery). */
    const [addrSuggestions, setAddrSuggestions] = useState<GeoSuggestion[]>([]);
    const addrDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

    const set = (k: keyof WizardForm, v: string) => setForm((p) => ({ ...p, [k]: v }));
    // Manual edit of a location-bearing field (número/bairro/cidade/estado) invalidates a
    // previously picked autocomplete point → clear coords so discovery geocodes the FINAL
    // address instead of keeping the stale picked location (e.g. city changed after pick).
    const setAddrField = (k: 'number' | 'neighborhood' | 'city' | 'state', v: string) =>
        setForm((p) => ({ ...p, [k]: v, latitude: null, longitude: null }));
    const setDay = (key: string, patch: Partial<DayHours>) =>
        setHours((p) => ({ ...p, [key]: { ...p[key], ...patch } }));

    // CEP typed → format, and once complete (8 digits) autofill street/bairro/city/UF.
    const onCepChange = async (raw: string) => {
        const formatted = formatCep(onlyDigits(raw).slice(0, 8));
        set('zipCode', formatted);
        if (onlyDigits(formatted).length !== 8) return;
        setCepLoading(true);
        try {
            const r = await lookupCep(formatted);
            if (r) {
                // Address changed via CEP (not a picked point) → drop coords so discovery re-geocodes.
                setForm((p) => ({
                    ...p,
                    address1: r.street || p.address1,
                    neighborhood: r.neighborhood || p.neighborhood,
                    city: r.city || p.city,
                    state: r.state || p.state,
                    latitude: null,
                    longitude: null,
                }));
            }
        } finally {
            setCepLoading(false);
        }
    };

    // Canadian postal code → format "A1A 1A1" and, once complete, prefill city +
    // province (street isn't in any free tier, so the owner types it).
    const onCaPostalChange = async (raw: string) => {
        const formatted = formatCaPostal(raw);
        set('zipCode', formatted);
        if (formatted.replace(/[^A-Za-z0-9]/g, '').length < 6) return;
        setCepLoading(true);
        try {
            const r = await lookupCaPostal(formatted);
            if (r) {
                setForm((p) => ({ ...p, city: r.city || p.city, state: r.state || p.state, latitude: null, longitude: null }));
            }
        } finally {
            setCepLoading(false);
        }
    };

    // Street field: type → debounced MapTiler autocomplete (biased to the picked
    // country). Picking a suggestion fills street + city + state + postal.
    const onAddressInput = (raw: string) => {
        // Typing invalidates a previously picked point → clear coords (re-geocode on save).
        setForm((p) => ({ ...p, address1: raw, latitude: null, longitude: null }));
        if (addrDebounce.current) clearTimeout(addrDebounce.current);
        if (raw.trim().length < 3) { setAddrSuggestions([]); return; }
        addrDebounce.current = setTimeout(async () => {
            const s = await geocodeSuggest(raw, form.country);
            setAddrSuggestions(s);
        }, 300);
    };

    const pickAddressSuggestion = (s: GeoSuggestion) => {
        setForm((p) => {
            const isBR = p.country === 'BR';
            // BR breaks the address into street + number fields; international puts
            // the whole line into address1 ("430 Westview Street") — no separate
            // number/neighborhood fields on that form.
            const fullLine = s.houseNumber ? `${s.houseNumber} ${s.label}` : s.label;
            return {
                ...p,
                address1: (isBR ? s.label : fullLine) || p.address1,
                number: isBR ? (s.houseNumber || p.number) : p.number,
                neighborhood: isBR ? (s.neighborhood || p.neighborhood) : p.neighborhood,
                city: s.city || p.city,
                // Geocoders return the state NAME; the product stores the UF (what
                // ViaCEP fills). Unknown name → keep what the owner typed.
                state: (isBR ? brStateToUf(s.region) : s.region) || p.state,
                zipCode: s.postcode || p.zipCode,
                // Persist the exact picked point so discovery skips re-geocoding.
                latitude: s.latitude ?? null,
                longitude: s.longitude ?? null,
            };
        });
        setAddrSuggestions([]);
    };

    // ── CNPJ lookup ─────────────────────────────────────────────────────────
    const handleCnpjSearch = async () => {
        setCnpjError('');
        setCnpjResult(null);
        if (!isValidCnpj(cnpjInput)) {
            setCnpjError(t('onboarding.cnpj.invalid'));
            return;
        }
        try {
            setCnpjLoading(true);
            const result = await lookupCnpj(cnpjInput);
            setCnpjResult(result);
        } catch (e) {
            setCnpjError(e instanceof CnpjNotFoundError ? t('onboarding.cnpj.notFound') : t('onboarding.cnpj.error'));
        } finally {
            setCnpjLoading(false);
        }
    };

    // Apply the fetched company to the form, then move to the ownership-verification phase.
    // Start from EMPTY so leftovers from a previous method (e.g. a prior Google pick)
    // never bleed into the CNPJ data.
    const applyCnpjResult = () => {
        if (!cnpjResult) return;
        setForm({
            ...EMPTY,
            name: cnpjResult.name,
            legalName: cnpjResult.legalName,
            cnpj: cnpjResult.cnpj,
            registeredEmail: cnpjResult.email,
            registeredPhone: cnpjResult.phone,
            cnae: cnpjResult.cnae,
            // CNAE 86xx/87xx (atenção à saúde) or a health-sounding description
            // pre-suggests health mode — visible suggestion, owner still decides.
            healthAnswer: /sa[úu]de|m[ée]dic|odont|psic[óo]|fisioter|cl[íi]nic/i.test(cnpjResult.cnae || '')
                ? 'yes' : '',
            cnpjSnapshot: cnpjResult.snapshot,
            city: cnpjResult.city,
            state: cnpjResult.state,
            address1: cnpjResult.street,
            number: cnpjResult.number,
            complement: cnpjResult.complement,
            neighborhood: cnpjResult.neighborhood,
            zipCode: cnpjResult.zipCode,
        });
        setCnpjPhase('verify');
        setOtpError('');
        setOtpCode('');
        setOtpVerified(false);

        const email = hasUsableEmail(cnpjResult.email);
        const phone = !!cnpjResult.phone;
        if (email && phone) {
            // Both channels available → let the user choose.
            setOtpStatus('choose');
        } else if (email) {
            startEmailOtp(cnpjResult.cnpj);
        } else if (phone) {
            startSmsDemo(cnpjResult.phone);
        } else {
            setOtpStatus('nocontact');
        }
    };

    // Send the email OTP (real — via the backend, to the Receita-registered address).
    const startEmailOtp = async (cnpj: string) => {
        setOtpStatus('sending');
        setOtpError('');
        setOtpCode('');
        try {
            const token = await getAccessTokenSilently();
            const res = await requestCnpjOtp(cnpj, token);
            if (res.status === 'AUTO_VERIFIED') {
                setOtpVerified(true);
                setOtpStatus('auto');
            } else if (res.status === 'SENT') {
                setOtpMasked(res.maskedEmail || '');
                setOtpStatus('sent');
            } else {
                // Unexpected (backend found no email) — fall back to SMS if possible.
                if (cnpjResult?.phone) startSmsDemo(cnpjResult.phone);
                else setOtpStatus('nocontact');
            }
        } catch {
            // Verification is optional — a failure shouldn't block onboarding.
            if (cnpjResult?.phone) startSmsDemo(cnpjResult.phone);
            else setOtpStatus('nocontact');
        }
    };

    // Show the SMS PIN screen (fake until an SMS provider is connected).
    const startSmsDemo = (phone: string) => {
        setOtpMasked(maskPhone(phone));
        setOtpCode('');
        setOtpStatus('smsdemo');
    };

    // Validate the typed OTP.
    const submitOtp = async () => {
        if (otpCode.trim().length < 4) return;
        // SMS path is a UI demo until a provider is connected — accept and continue.
        if (otpStatus === 'smsdemo') { continueFromCnpj(); return; }
        if (!form.cnpj) return;
        setOtpError('');
        setOtpVerifying(true);
        try {
            const token = await getAccessTokenSilently();
            const res = await verifyCnpjOtp(form.cnpj, otpCode.trim(), token);
            if (res.verified) {
                setOtpVerified(true);
            } else {
                setOtpError(t('onboarding.verify.wrongCode'));
            }
        } catch {
            setOtpError(t('onboarding.verify.error'));
        } finally {
            setOtpVerifying(false);
        }
    };

    // Continue from the CNPJ flow into the named steps (verified or not).
    const continueFromCnpj = () => {
        setSubView(null);
        setStep(1);
    };

    // ── Google import (fake) ────────────────────────────────────────────────
    const handleGoogleConnect = () => {
        setGoogleConnecting(true);
        // Simulate the OAuth round-trip.
        setTimeout(() => {
            setGoogleConnecting(false);
            setGoogleConnected(true);
        }, 900);
    };

    const applyGoogleBusiness = (g: Partial<WizardForm>) => {
        // Clean slate so a prior CNPJ attempt's fields don't bleed into the Google pick.
        setForm({ ...EMPTY, ...g });
        setSubView(null);
        setStep(1);
    };

    // Return to the 3-card chooser, clearing sub-view scratch state AND any prefill
    // (so picking "manual" next starts from a blank form, not the CNPJ data).
    const backToChooser = () => {
        setSubView(null);
        setForm(EMPTY);
        setCnpjInput('');
        setCnpjError('');
        setCnpjResult(null);
        setCnpjPhase('lookup');
        setOtpStatus('idle');
        setOtpMasked('');
        setOtpCode('');
        setOtpError('');
        setOtpVerified(false);
        setGoogleConnected(false);
        setGoogleConnecting(false);
    };

    const last = STEPS.length - 1;
    const canNext = (step !== 1 || form.name.trim().length > 0) // name required on step 1
        && (step !== 2 || ((!HEALTH_RECORDS_ENABLED || form.healthAnswer !== '')
            // "Outro" needs the free text — an empty custom category would leave
            // the business uncategorized in the marketplace (QA decision 2026-07-04).
            && (form.category !== 'Outro' || form.categoryOther.trim().length > 0)))
        // Full address required to proceed (QA decision 2026-07-04): street, city
        // and state — the minimum for the marketplace geo listing to make sense.
        && (step !== 3 || (form.address1.trim().length > 0
            && form.city.trim().length > 0
            && form.state.trim().length > 0));

    const next = () => setStep((s) => Math.min(s + 1, last));
    const back = () => setStep((s) => Math.max(s - 1, 0));

    const finish = async () => {
        if (saving) return; // double-click guard: one click = one business
        if (!form.name.trim()) {
            setAlert('danger', t('onboarding.alerts.nameRequired'));
            setStep(1);
            return;
        }
        if (!agreedTerms) {
            setAlert('danger', t('onboarding.terms.required'));
            return;
        }
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            // From WHERE the business is, never from the browser of whoever is
            // typing: a Petrópolis business created from Vancouver went live in
            // America/Vancouver, with every public slot four hours off.
            const timezone = timezoneFor(form.country, form.state);

            // Compose the structured address into the two stored lines:
            //   address1 = "Logradouro, Número"   address2 = "Complemento, Bairro"
            const addrLine1 = [form.address1.trim(), form.number.trim()].filter(Boolean).join(', ') || null;
            const addrLine2 = [form.complement.trim(), form.neighborhood.trim()].filter(Boolean).join(', ') || null;

            // 1) Spin up the business (owner derived from JWT on the backend).
            const created = await createBusiness(
                {
                    name: form.name.trim(),
                    cnpj: form.cnpj.trim() || null,
                    legalName: form.legalName.trim() || null,
                    registeredEmail: form.registeredEmail.trim() || null,
                    registeredPhone: form.registeredPhone.trim() || null,
                    cnae: form.cnae.trim() || null,
                    cnpjSnapshot: form.cnpjSnapshot.trim() || null,
                    termsAccepted: agreedTerms,
                    termsVersion: agreedTerms ? TERMS_VERSION : null,
                    language: 'Portuguese',
                    timezone,
                    currency: currencyForCountry(form.country),
                    city: form.city.trim() || null,
                    state: form.state.trim() || null,
                    country: canonicalCountry(form.country),
                    address1: addrLine1,
                    address2: addrLine2,
                    zipCode: form.zipCode.trim() || null,
                    latitude: form.latitude,
                    longitude: form.longitude,
                },
                token
            );

            // 2) Fill the public profile (same fields the Settings → Public Profile
            //    tab edits) and mark onboarding complete.
            const details = {
                id: Number(created.id),
                name: form.name.trim(),
                language: 'Portuguese',
                timezone,
                currency: currencyForCountry(form.country),
                brandColor: form.brandColor || null,
                city: form.city.trim() || null,
                state: form.state.trim() || null,
                country: canonicalCountry(form.country),
                address1: addrLine1,
                address2: addrLine2,
                zipCode: form.zipCode.trim() || null,
                latitude: form.latitude,
                longitude: form.longitude,
                category: (form.category === 'Outro' ? (form.categoryOther.trim() || 'Outro') : form.category) || null,
                headline: form.headline.trim() || null,
                about: form.about.trim() || null,
                yearsExperience: form.yearsExperience === '' ? null : Number(form.yearsExperience),
                highlight: form.highlight.trim() || null,
                coverImage: form.coverImage.trim() || null,
                instagram: form.instagram.trim() || null,
                onboardingComplete: true,
            } as unknown as BusinessDetailsInput;

            const detailsResult = await saveBusinessDetails(details, token);
            if (!detailsResult.success) {
                // Stop here: pretending success with half-saved data is worse than
                // an honest error (the business exists; details can be retried).
                throw new Error(detailsResult.error || t('onboarding.alerts.failCreate'));
            }

            // 2b) Prontuário master switch — only persisted when the owner said
            //     YES (backend default is already false). Non-fatal on failure.
            if (HEALTH_RECORDS_ENABLED && form.healthAnswer === 'yes') {
                try {
                    const st = await findBusinessSettingByBusinessId(Number(created.id), token);
                    if (st) {
                        const input: BusinessSettingsInput = mapSourceToTarget({ ...st, healthMode: true }, BusinessSettingsInput);
                        await saveBusinessSettings(input, token);
                    }
                } catch { /* owner can flip it later in Configurações → Empresa */ }
            }

            // 3) Working hours (optional — skipped users configure them later).
            //    The backend parses 12-hour "hh:mm AM/PM"; convert from the 24h
            //    <input type="time"> value before sending.
            if (!skipHours) {
                const hoursInput = DAYS
                    .filter((d) => hours[d.key].open)
                    .map((d) => ({
                        id: null,
                        dayOfWeek: d.key,
                        openTime: to12h(hours[d.key].openTime),
                        closeTime: to12h(hours[d.key].closeTime),
                        businessId: Number(created.id),
                    })) as unknown as BusinessHoursInput[];
                if (hoursInput.length > 0) {
                    const res = await saveBusinessHours(hoursInput, token);
                    if (!res || (res as { success?: boolean }).success === false) {
                        // Non-fatal — the business is created; hours can be set later.
                        setAlert('danger', t('onboarding.alerts.hoursFailed'));
                    }
                }
            }

            setAlert('success', t('onboarding.alerts.created', { name: created.name }));
            onDone({ ...created, brandColor: form.brandColor || created.brandColor });
        } catch (e) {
            // QA 4.1: never surface raw technical errors (SQL, stack traces).
            const msg = e instanceof Error ? e.message : '';
            const technical = /sql|grammar|exception|constraint|violat|stack|r2dbc|postgres/i.test(msg);
            if (technical) console.error('[onboarding] create failed:', msg);
            setAlert('danger', !msg || technical ? t('onboarding.alerts.failCreate') : msg);
        } finally {
            setSaving(false);
        }
    };

    // Step-3 address fields, composed differently per country (BR = CEP-first;
    // CA = one address search that fills number/street/neighborhood/city/state/postal).
    const countryField = (
        <label className="ob-label">{t('onboarding.step3.country')}
            <select className="ob-input" value={form.country} onChange={(e) => set('country', e.target.value)} data-cy="onboard-country-input">
                {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{t(c.nameKey)}</option>
                ))}
            </select>
        </label>
    );
    const cepField = (
        <label className="ob-label">{t('onboarding.step3.zipCode')}
            <input className="ob-input" value={form.zipCode} onChange={(e) => onCepChange(e.target.value)} placeholder={t('onboarding.step3.zipCodePlaceholder')} inputMode="numeric" data-cy="onboard-zipcode-input" />
            {cepLoading && <span className="ob-cep-hint">{t('onboarding.step3.cepLoading')}</span>}
        </label>
    );
    const postalField = (
        <label className="ob-label">{t('onboarding.step3.postalCode')}
            <input className="ob-input" value={form.zipCode} onChange={(e) => onCaPostalChange(e.target.value)} placeholder={t('onboarding.step3.postalCodePlaceholder')} data-cy="onboard-zipcode-input" />
            {cepLoading && <span className="ob-cep-hint">{t('onboarding.step3.cepLoading')}</span>}
        </label>
    );
    const addressField = (
        <label className="ob-label">{form.country === 'BR' ? t('onboarding.step3.address') : t('onboarding.step3.addressLine1')}
            <div className="ob-ac">
                <input
                    className="ob-input"
                    maxLength={180}
                    value={form.address1}
                    onChange={(e) => onAddressInput(e.target.value)}
                    onBlur={() => setTimeout(() => setAddrSuggestions([]), 150)}
                    placeholder={form.country === 'BR' ? t('onboarding.step3.addressPlaceholder') : t('onboarding.step3.addressLine1Placeholder')}
                    autoComplete="off"
                    data-cy="onboard-address-input"
                />
                {addrSuggestions.length > 0 && (
                    <ul className="ob-ac__list" data-cy="onboard-address-suggestions">
                        {addrSuggestions.map((s, i) => (
                            <li key={i} className="ob-ac__item" onMouseDown={() => pickAddressSuggestion(s)}>
                                <span className="ob-ac__label">{s.label}</span>
                                {s.context && <span className="ob-ac__ctx">{s.context}</span>}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </label>
    );
    const numberField = (
        <label className="ob-label">{t('onboarding.step3.number')}
            <input className="ob-input" maxLength={20} value={form.number} onChange={(e) => setAddrField('number', e.target.value)} placeholder={t('onboarding.step3.numberPlaceholder')} data-cy="onboard-number-input" />
        </label>
    );
    const complementField = (
        <label className="ob-label">{form.country === 'BR' ? t('onboarding.step3.complement') : t('onboarding.step3.addressLine2')}
            <input className="ob-input" maxLength={100} value={form.complement} onChange={(e) => set('complement', e.target.value)} placeholder={form.country === 'BR' ? t('onboarding.step3.complementPlaceholder') : t('onboarding.step3.addressLine2Placeholder')} data-cy="onboard-complement-input" />
        </label>
    );
    const neighborhoodField = (
        <label className="ob-label">{t('onboarding.step3.neighborhood')}
            <input className="ob-input" maxLength={100} value={form.neighborhood} onChange={(e) => setAddrField('neighborhood', e.target.value)} placeholder={t('onboarding.step3.neighborhoodPlaceholder')} data-cy="onboard-neighborhood-input" />
        </label>
    );
    const cityField = (
        <label className="ob-label">{t('onboarding.step3.city')}
            <input className="ob-input" maxLength={120} value={form.city} onChange={(e) => setAddrField('city', e.target.value)} placeholder={t('onboarding.step3.cityPlaceholder')} data-cy="onboard-city-input" />
        </label>
    );
    const stateField = (
        <label className="ob-label">{t('onboarding.step3.state')}
            {form.country === 'BR' ? (
                <select className="ob-input" value={form.state} onChange={(e) => setAddrField('state', e.target.value)} data-cy="onboard-state-input">
                    <option value="">{t('onboarding.step3.statePlaceholder')}</option>
                    {BR_STATES.map((s) => (
                        <option key={s.uf} value={s.uf}>{s.uf} — {s.name}</option>
                    ))}
                </select>
            ) : (
                <input className="ob-input" maxLength={60} value={form.state} onChange={(e) => setAddrField('state', e.target.value)} placeholder={t('onboarding.step3.stateIntlPlaceholder')} data-cy="onboard-state-input" />
            )}
        </label>
    );

    return (
        <div className="ob-wrap" data-cy="onboard-page">
            {/* Progress sidebar */}
            <aside className="ob-side">
                <div className="ob-side__brand">
                    <span
                        className="ob-side__logo"
                        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/logo192.png)` }}
                    />
                    getservice
                </div>
                {/* Language first — a non-Portuguese owner switches here before any
                    field label (e.g. "Logradouro") would trip them up. */}
                <div className="ob-lang" role="group" aria-label={t('onboarding.language')}>
                    <button
                        type="button"
                        className={`ob-lang__btn ${i18n.language?.startsWith('pt') ? 'is-active' : ''}`}
                        onClick={() => setLang('pt')}
                        data-cy="onboard-lang-pt"
                    >PT</button>
                    <button
                        type="button"
                        className={`ob-lang__btn ${i18n.language?.startsWith('en') ? 'is-active' : ''}`}
                        onClick={() => setLang('en')}
                        data-cy="onboard-lang-en"
                    >EN</button>
                </div>
                <ul className="ob-side__steps">
                    {STEPS.map((label, i) => (
                        <li key={label} className={`ob-side__step ${i === step ? 'is-active' : ''} ${i < step ? 'is-done' : ''}`} data-cy={`onboard-step-${i}`}>
                            <span className="ob-side__dot">{i < step ? '✓' : i + 1}</span>
                            {t(`onboarding.steps.${i}`)}
                        </li>
                    ))}
                </ul>
                {allowCancel && (
                    <button type="button" className="ob-exit" onClick={onCancel} disabled={saving} data-cy="onboard-cancel-btn">
                        {t('onboarding.exit')}
                    </button>
                )}
            </aside>

            {/* Step body */}
            <main className="ob-main">
                <div className="ob-step">
                    {step === 0 && subView === null && (
                        <div className="ob-center">
                            <div className="ob-emoji">🚀</div>
                            <h1 className="ob-title">{t('onboarding.method.title')}</h1>
                            <p className="ob-sub">{t('onboarding.method.sub')}</p>
                            <div className="ob-methods">
                                <button type="button" className="ob-method" onClick={() => setSubView('google')} data-cy="onboard-method-google">
                                    <span className="ob-method__icon">🌐</span>
                                    <span className="ob-method__body">
                                        <span className="ob-method__title">{t('onboarding.method.googleTitle')}</span>
                                        <span className="ob-method__desc">{t('onboarding.method.googleDesc')}</span>
                                    </span>
                                </button>
                                <button type="button" className="ob-method" onClick={() => setSubView('cnpj')} data-cy="onboard-method-cnpj">
                                    <span className="ob-method__icon">🧾</span>
                                    <span className="ob-method__body">
                                        <span className="ob-method__title">
                                            {t('onboarding.method.cnpjTitle')}
                                            <span className="ob-method__tag">{t('onboarding.method.recommended')}</span>
                                        </span>
                                        <span className="ob-method__desc">{t('onboarding.method.cnpjDesc')}</span>
                                    </span>
                                </button>
                                <button type="button" className="ob-method" onClick={() => { setForm(EMPTY); setStep(1); }} data-cy="onboard-method-manual">
                                    <span className="ob-method__icon">✍️</span>
                                    <span className="ob-method__body">
                                        <span className="ob-method__title">{t('onboarding.method.manualTitle')}</span>
                                        <span className="ob-method__desc">{t('onboarding.method.manualDesc')}</span>
                                    </span>
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 0 && subView === 'cnpj' && cnpjPhase === 'lookup' && (
                        <div className="ob-sub-view" data-cy="onboard-cnpj-view">
                            <h1 className="ob-title">{t('onboarding.cnpj.title')}</h1>
                            <div className="ob-cnpj-row">
                                <input
                                    className="ob-input"
                                    value={cnpjInput}
                                    onChange={(e) => setCnpjInput(formatCnpj(cleanCnpj(e.target.value)))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleCnpjSearch(); }}
                                    placeholder={t('onboarding.cnpj.placeholder')}
                                    autoCapitalize="characters"
                                    autoFocus
                                    data-cy="onboard-cnpj-input"
                                />
                                <button type="button" className="ob-btn" onClick={handleCnpjSearch} disabled={cnpjLoading} data-cy="onboard-cnpj-search">
                                    {cnpjLoading ? t('onboarding.cnpj.searching') : t('onboarding.cnpj.search')}
                                </button>
                            </div>
                            {cnpjError && <div className="ob-error" data-cy="onboard-cnpj-error">{cnpjError}</div>}
                            {cnpjResult && (
                                <div className="ob-result" data-cy="onboard-cnpj-result">
                                    <div className="ob-result__row"><span>{t('onboarding.cnpj.resultLabelLegal')}</span><strong>{cnpjResult.legalName || '—'}</strong></div>
                                    {cnpjResult.name !== cnpjResult.legalName && (
                                        <div className="ob-result__row"><span>{t('onboarding.cnpj.resultLabelFantasy')}</span><strong>{cnpjResult.name}</strong></div>
                                    )}
                                    <div className="ob-result__row"><span>{t('onboarding.cnpj.resultLabelAddress')}</span><strong>{[cnpjResult.address1, [cnpjResult.city, cnpjResult.state].filter(Boolean).join(' / ')].filter(Boolean).join(' — ') || '—'}</strong></div>
                                    {cnpjResult.phone && <div className="ob-result__row"><span>{t('onboarding.cnpj.resultLabelPhone')}</span><strong>{cnpjResult.phone}</strong></div>}
                                    {cnpjResult.email && <div className="ob-result__row"><span>{t('onboarding.cnpj.resultLabelEmail')}</span><strong>{cnpjResult.email}</strong></div>}
                                    <div className="ob-result__row"><span>{t('onboarding.cnpj.resultLabelStatus')}</span><strong>{cnpjResult.situation || '—'}</strong></div>
                                    {!cnpjResult.active && <div className="ob-error">{t('onboarding.cnpj.inactiveWarn')}</div>}
                                    <p className="ob-result-note">{t('onboarding.cnpj.editableNote')}</p>
                                    <button type="button" className="ob-btn" style={{ marginTop: '0.4rem' }} onClick={applyCnpjResult} data-cy="onboard-cnpj-use">
                                        {t('onboarding.cnpj.use')}
                                    </button>
                                </div>
                            )}
                            <button type="button" className="ob-back-link" onClick={backToChooser} data-cy="onboard-cnpj-back">{t('onboarding.method.back')}</button>
                        </div>
                    )}

                    {step === 0 && subView === 'cnpj' && cnpjPhase === 'verify' && (
                        <div className="ob-sub-view" data-cy="onboard-verify-view">
                            <h1 className="ob-title">{t('onboarding.verify.title')}</h1>

                            {otpVerified ? (
                                <>
                                    <div className="ob-verify-badge" data-cy="onboard-verify-ok">✓ {t('onboarding.verify.verified')}</div>
                                    <button type="button" className="ob-btn" style={{ marginTop: '1rem' }} onClick={continueFromCnpj} data-cy="onboard-verify-continue">
                                        {t('onboarding.verify.continue')}
                                    </button>
                                </>
                            ) : otpStatus === 'choose' ? (
                                <>
                                    <p className="ob-sub">{t('onboarding.verify.chooseSub')}</p>
                                    <div className="ob-methods">
                                        <button type="button" className="ob-method" onClick={() => startEmailOtp(form.cnpj)} data-cy="onboard-verify-pick-email">
                                            <span className="ob-method__icon">✉️</span>
                                            <span className="ob-method__body">
                                                <span className="ob-method__title">{t('onboarding.verify.byEmail')}</span>
                                                <span className="ob-method__desc">{maskEmail(cnpjResult?.email || '')}</span>
                                            </span>
                                        </button>
                                        <button type="button" className="ob-method" onClick={() => startSmsDemo(cnpjResult?.phone || '')} data-cy="onboard-verify-pick-sms">
                                            <span className="ob-method__icon">📱</span>
                                            <span className="ob-method__body">
                                                <span className="ob-method__title">{t('onboarding.verify.bySms')}</span>
                                                <span className="ob-method__desc">{maskPhone(cnpjResult?.phone || '')}</span>
                                            </span>
                                        </button>
                                    </div>
                                    <button type="button" className="ob-back-link" onClick={continueFromCnpj} data-cy="onboard-verify-choose-skip">{t('onboarding.verify.skip')}</button>
                                </>
                            ) : otpStatus === 'sending' ? (
                                <p className="ob-sub">{t('onboarding.verify.sending')}</p>
                            ) : otpStatus === 'sent' ? (
                                <>
                                    <p className="ob-sub">{t('onboarding.verify.sentTo', { email: otpMasked })}</p>
                                    <div className="ob-cnpj-row">
                                        <input
                                            className="ob-input"
                                            value={otpCode}
                                            onChange={(e) => setOtpCode(e.target.value.replace(/\D+/g, '').slice(0, 6))}
                                            onKeyDown={(e) => { if (e.key === 'Enter') submitOtp(); }}
                                            placeholder={t('onboarding.verify.codePlaceholder')}
                                            inputMode="numeric"
                                            autoFocus
                                            data-cy="onboard-verify-code"
                                        />
                                        <button type="button" className="ob-btn" onClick={submitOtp} disabled={otpVerifying || otpCode.trim().length < 4} data-cy="onboard-verify-submit">
                                            {otpVerifying ? t('onboarding.verify.checking') : t('onboarding.verify.confirm')}
                                        </button>
                                    </div>
                                    {otpError && <div className="ob-error" data-cy="onboard-verify-error">{otpError}</div>}
                                    <p className="ob-demo-note">{t('onboarding.verify.contactNote')}</p>
                                    {hasUsableEmail(cnpjResult?.email) && !!cnpjResult?.phone && (
                                        <button type="button" className="ob-back-link" onClick={() => setOtpStatus('choose')} data-cy="onboard-verify-change-email">{t('onboarding.verify.changeMethod')}</button>
                                    )}
                                    <button type="button" className="ob-back-link" onClick={continueFromCnpj} data-cy="onboard-verify-skip">{t('onboarding.verify.skip')}</button>
                                </>
                            ) : otpStatus === 'smsdemo' ? (
                                <>
                                    <p className="ob-sub">{t('onboarding.verify.smsSentTo', { phone: otpMasked })}</p>
                                    <div className="ob-cnpj-row">
                                        <input
                                            className="ob-input"
                                            value={otpCode}
                                            onChange={(e) => setOtpCode(e.target.value.replace(/\D+/g, '').slice(0, 6))}
                                            onKeyDown={(e) => { if (e.key === 'Enter') submitOtp(); }}
                                            placeholder={t('onboarding.verify.codePlaceholder')}
                                            inputMode="numeric"
                                            autoFocus
                                            data-cy="onboard-verify-sms-code"
                                        />
                                        <button type="button" className="ob-btn" onClick={submitOtp} disabled={otpCode.trim().length < 4} data-cy="onboard-verify-sms-submit">
                                            {t('onboarding.verify.confirm')}
                                        </button>
                                    </div>
                                    <p className="ob-demo-note">{t('onboarding.verify.smsDemoNote')}</p>
                                    {hasUsableEmail(cnpjResult?.email) && !!cnpjResult?.phone && (
                                        <button type="button" className="ob-back-link" onClick={() => setOtpStatus('choose')} data-cy="onboard-verify-change-sms">{t('onboarding.verify.changeMethod')}</button>
                                    )}
                                    <button type="button" className="ob-back-link" onClick={continueFromCnpj} data-cy="onboard-verify-sms-skip">{t('onboarding.verify.skip')}</button>
                                </>
                            ) : (
                                // 'nocontact'
                                <>
                                    <p className="ob-sub">{t('onboarding.verify.noContact')}</p>
                                    <button type="button" className="ob-btn" onClick={continueFromCnpj} data-cy="onboard-verify-continue-nc">
                                        {t('onboarding.verify.continue')}
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    {step === 0 && subView === 'google' && (
                        <div className="ob-sub-view" data-cy="onboard-google-view">
                            <h1 className="ob-title">{t('onboarding.google.title')}</h1>
                            {!googleConnected ? (
                                <>
                                    <p className="ob-sub">{t('onboarding.google.sub')}</p>
                                    <button type="button" className="ob-btn" onClick={handleGoogleConnect} disabled={googleConnecting} data-cy="onboard-google-connect">
                                        {googleConnecting ? t('onboarding.google.connecting') : t('onboarding.google.connect')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <p className="ob-sub">{t('onboarding.google.pickTitle')}</p>
                                    <div className="ob-glist">
                                        {FAKE_GOOGLE_BUSINESSES.map((g) => (
                                            <button key={g.name} type="button" className="ob-method" onClick={() => applyGoogleBusiness(g)} data-cy="onboard-google-pick">
                                                <span className="ob-method__icon">🏢</span>
                                                <span className="ob-method__body">
                                                    <span className="ob-method__title">{g.name}</span>
                                                    <span className="ob-method__desc">{[[g.address1, g.number].filter(Boolean).join(', '), g.city].filter(Boolean).join(' — ')}</span>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                    <p className="ob-demo-note">{t('onboarding.google.demoNote')}</p>
                                </>
                            )}
                            <button type="button" className="ob-back-link" onClick={backToChooser} data-cy="onboard-google-back">{t('onboarding.method.back')}</button>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="ob-form">
                            <h1 className="ob-title">{t('onboarding.step1.title')}</h1>
                            <input
                                className="ob-input ob-input--lg"
                                maxLength={255}
                                value={form.name}
                                onChange={(e) => set('name', e.target.value)}
                                placeholder={t('onboarding.step1.namePlaceholder')}
                                autoFocus
                                data-cy="onboard-name-input"
                            />
                        </div>
                    )}

                    {step === 2 && (
                        <div className="ob-form">
                            <h1 className="ob-title">{t('onboarding.step2.title')}</h1>
                            <div className="ob-grid">
                                {CATEGORIES.map((c) => (
                                    <button
                                        key={c.value}
                                        type="button"
                                        className={`ob-chip ${form.category === c.value ? 'is-selected' : ''}`}
                                        onClick={() => {
                                            set('category', c.value);
                                            if (!form.healthAnswer && c.value === 'Clínica') set('healthAnswer', 'yes');
                                        }}
                                        data-cy={`onboard-category-${c.labelKey}`}
                                    >
                                        {t(`onboarding.categories.${c.labelKey}`)}
                                    </button>
                                ))}
                            </div>
                            {form.category === 'Outro' && (
                                <input
                                    className="ob-input"
                                    style={{ marginTop: '1rem' }}
                                    type="text"
                                    autoFocus
                                    maxLength={80}
                                    value={form.categoryOther}
                                    onChange={(e) => set('categoryOther', e.target.value)}
                                    placeholder={t('onboarding.step2.otherPlaceholder')}
                                    data-cy="onboard-category-other-input"
                                />
                            )}
                            {HEALTH_RECORDS_ENABLED && (
                            <div style={{ marginTop: '1.4rem' }} data-cy="onboard-health-question">
                                <div className="ob-subtitle">{t('onboarding.step2.healthQuestion')}</div>
                                <div className="ob-grid" style={{ marginTop: '0.5rem' }}>
                                    <button type="button"
                                        className={`ob-chip ${form.healthAnswer === 'yes' ? 'is-selected' : ''}`}
                                        onClick={() => set('healthAnswer', 'yes')} data-cy="onboard-health-yes">
                                        {t('common.yes')}
                                    </button>
                                    <button type="button"
                                        className={`ob-chip ${form.healthAnswer === 'no' ? 'is-selected' : ''}`}
                                        onClick={() => set('healthAnswer', 'no')} data-cy="onboard-health-no">
                                        {t('common.no')}
                                    </button>
                                </div>
                                <small className="text-muted">{t('onboarding.step2.healthHint')}</small>
                            </div>
                            )}
                        </div>
                    )}

                    {step === 3 && (
                        <div className="ob-form">
                            <h1 className="ob-title">{t('onboarding.step3.title')}</h1>
                            {form.country === 'BR' ? (
                                <>
                                    {/* Brazil: CEP first → autofills street/city/state. */}
                                    <div className="ob-row">{countryField}{cepField}</div>
                                    <div className="ob-row">{addressField}{numberField}</div>
                                    <div className="ob-row">{complementField}{neighborhoodField}</div>
                                    <div className="ob-row">{cityField}{stateField}</div>
                                </>
                            ) : (
                                <>
                                    {/* Non-BR (Canada/international): line-based address (Address 1 /
                                        Address 2 / City / Province / Postal) — NOT the Brazilian
                                        street+number+neighborhood breakdown. The ADDRESS SEARCH is the
                                        entry point; one pick fills line 1 (incl. number) + city +
                                        province + postal. Saved to the same DB columns. */}
                                    <div className="ob-row">{countryField}{addressField}</div>
                                    <div className="ob-row">{complementField}{cityField}</div>
                                    <div className="ob-row">{stateField}{postalField}</div>
                                </>
                            )}
                        </div>
                    )}

                    {step === 4 && (
                        <div className="ob-form">
                            <div className="ob-hours-head">
                                <h1 className="ob-title" style={{ marginBottom: 0 }}>{t('onboarding.step4.title')}</h1>
                                <button
                                    type="button"
                                    className="ob-skip"
                                    onClick={() => { setSkipHours(true); next(); }}
                                    data-cy="onboard-skip-hours-btn"
                                >
                                    {t('onboarding.step4.skip')}
                                </button>
                            </div>
                            <p className="ob-sub">{t('onboarding.step4.sub')}</p>
                            <div className="ob-hours">
                                {DAYS.map((d) => {
                                    const h = hours[d.key];
                                    return (
                                        <div key={d.key} className={`ob-hours__row ${h.open ? '' : 'is-closed'}`}>
                                            <label className="ob-hours__day">
                                                <input
                                                    type="checkbox"
                                                    checked={h.open}
                                                    onChange={(e) => { setSkipHours(false); setDay(d.key, { open: e.target.checked }); }}
                                                    data-cy={`onboard-hours-${d.key.toLowerCase()}-toggle`}
                                                />
                                                {t(`onboarding.days.${d.key}`)}
                                            </label>
                                            {h.open ? (
                                                <div className="ob-hours__times">
                                                    {/* Shared TimeSelect (project standard — never native time
                                                        inputs): labels follow the business time format, which
                                                        defaults to 24h before a business exists (issues #66/#67). */}
                                                    <TimeSelect value={h.openTime} onChange={(v) => setDay(d.key, { openTime: v })} data-cy={`onboard-hours-${d.key.toLowerCase()}-open`} />
                                                    <span>{t('onboarding.step4.to')}</span>
                                                    <TimeSelect value={h.closeTime} onChange={(v) => setDay(d.key, { closeTime: v })} data-cy={`onboard-hours-${d.key.toLowerCase()}-close`} />
                                                </div>
                                            ) : (
                                                <span className="ob-hours__off">{t('onboarding.step4.closed')}</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {step === 5 && (
                        <div className="ob-form">
                            <h1 className="ob-title">{t('onboarding.step5.title')}</h1>
                            <label className="ob-label">{t('onboarding.step5.headline')}
                                <input className="ob-input" maxLength={160} value={form.headline} onChange={(e) => set('headline', e.target.value)} placeholder={t('onboarding.step5.headlinePlaceholder')} data-cy="onboard-headline-input" />
                            </label>
                            <label className="ob-label">{t('onboarding.step5.about')}
                                <textarea className="ob-input" rows={4} maxLength={1000} value={form.about} onChange={(e) => set('about', e.target.value)} placeholder={t('onboarding.step5.aboutPlaceholder')} data-cy="onboard-about-input" />
                                <small className="ob-char-count">{form.about.length}/1000</small>
                            </label>
                            <div className="ob-row">
                                <label className="ob-label">{t('onboarding.step5.yearsExperience')}
                                    {/* Digits-only text input: type="number" accepts 'e'/'+'/'-' and
                                        scientific notation (issue #68). Two digits = 0–99 by construction. */}
                                    <input className="ob-input" type="text" inputMode="numeric" maxLength={2} value={form.yearsExperience} onChange={(e) => set('yearsExperience', e.target.value.replace(/\D/g, ''))} placeholder="0" data-cy="onboard-years-experience-input" />
                                </label>
                                <label className="ob-label">{t('onboarding.step5.highlight')}
                                    <input className="ob-input" maxLength={200} value={form.highlight} onChange={(e) => set('highlight', e.target.value)} placeholder={t('onboarding.step5.highlightPlaceholder')} data-cy="onboard-highlight-input" />
                                </label>
                            </div>
                        </div>
                    )}

                    {step === 6 && (
                        <div className="ob-form">
                            <h1 className="ob-title">{t('onboarding.step6.title')}</h1>
                            <div className="ob-row">
                                <label className="ob-label">{t('onboarding.step6.brandColor')}
                                    <span className="ob-color">
                                        <input type="color" value={form.brandColor} onChange={(e) => set('brandColor', e.target.value)} data-cy="onboard-brand-color-input" />
                                        <code>{form.brandColor.toUpperCase()}</code>
                                    </span>
                                </label>
                            </div>
                            <label className="ob-label">{t('onboarding.step6.coverImage')}
                                <input className="ob-input" maxLength={500} value={form.coverImage} onChange={(e) => set('coverImage', e.target.value)} placeholder={t('onboarding.step6.coverImagePlaceholder')} data-cy="onboard-cover-image-input" />
                            </label>
                            {form.coverImage && (
                                // eslint-disable-next-line jsx-a11y/img-redundant-alt
                                <img src={mediaUrl(form.coverImage)} alt={t('onboarding.step6.coverPreviewAlt')} className="ob-cover-preview" />
                            )}
                            <label className="ob-label">{t('onboarding.step6.instagram')}
                                <input className="ob-input" maxLength={120} value={form.instagram} onChange={(e) => set('instagram', e.target.value)} placeholder={t('onboarding.step6.instagramPlaceholder')} data-cy="onboard-instagram-input" />
                            </label>
                        </div>
                    )}

                    {step === 7 && (
                        <div className="ob-form">
                            <h1 className="ob-title">{t('onboarding.step7.title')}</h1>
                            <p className="ob-sub">{t('onboarding.step7.sub')}</p>
                            <ul className="ob-review">
                                <li><span>{t('onboarding.step7.labelName')}</span><strong>{form.name || '—'}</strong></li>
                                <li><span>{t('onboarding.step7.labelService')}</span><strong>{(form.category === 'Outro' ? (form.categoryOther.trim() || 'Outro') : form.category) || '—'}</strong></li>
                                <li><span>{t('onboarding.step7.labelLocation')}</span><strong>{[form.city, form.state].filter(Boolean).join(', ') || '—'}</strong></li>
                                <li><span>{t('onboarding.step7.labelHours')}</span><strong>{skipHours ? t('onboarding.step7.configureLater') : t('onboarding.step7.daysOpen', { count: DAYS.filter((d) => hours[d.key].open).length })}</strong></li>
                                <li><span>{t('onboarding.step7.labelExperience')}</span><strong>{form.yearsExperience ? t('onboarding.step7.years', { count: Number(form.yearsExperience) }) : '—'}</strong></li>
                                {form.headline.trim() !== '' && <li><span>{t('onboarding.step7.labelHeadline')}</span><strong>{form.headline}</strong></li>}
                                {form.about.trim() !== '' && <li><span>{t('onboarding.step7.labelAbout')}</span><strong>{form.about.length > 80 ? form.about.slice(0, 80) + '…' : form.about}</strong></li>}
                                {form.highlight.trim() !== '' && <li><span>{t('onboarding.step7.labelHighlight')}</span><strong>{form.highlight}</strong></li>}
                            </ul>

                            {/* Veracity declaration (clickwrap) — required to create. */}
                            <label className="ob-terms" data-cy="onboard-terms">
                                <input
                                    type="checkbox"
                                    checked={agreedTerms}
                                    onChange={(e) => setAgreedTerms(e.target.checked)}
                                    data-cy="onboard-terms-check"
                                />
                                <span>{t('onboarding.terms.declaration')}</span>
                            </label>
                        </div>
                    )}
                </div>

                {/* Footer nav — hidden on step 0, where the method cards / sub-views
                    drive navigation themselves. */}
                {step > 0 && (
                    <div className="ob-footer">
                        <button type="button" className="ob-btn ob-btn--ghost" onClick={back} disabled={saving} data-cy="onboard-back-btn">{t('onboarding.back')}</button>
                        {step < last ? (
                            <button type="button" className="ob-btn" onClick={next} disabled={!canNext} data-cy="onboard-next-btn">
                                {t('onboarding.next')}
                            </button>
                        ) : (
                            <button type="button" className="ob-btn" onClick={finish} disabled={saving || !agreedTerms} data-cy="onboard-finish-btn">
                                {saving ? t('onboarding.creating') : t('onboarding.finish')}
                            </button>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};

export default BusinessOnboardingWizard;
